import { CHAT_UNREAD_BADGE_MAX } from '@/constants/chat';
import {
  ChatMemberRole,
  ChatMemberState,
  ChatPostingPolicy,
  ChatSenderRole,
  ChatServerOp,
  ConversationStatus,
  ConversationType,
  SharedObjectKind,
  type ChatConversationDetail,
  type ChatMembership,
  type ChatMessageView,
  type ChatServerFrame,
  type ChatSharedObject,
  type InboxItem,
} from '@/types/chat';

/** Roles that may change a chat's settings and see pending requests. */
const MODERATOR_ROLES = new Set<string>([
  ChatMemberRole.OWNER,
  ChatMemberRole.ADMIN,
  ChatMemberRole.ORGANISER,
]);

export const isModeratorRole = (role: string | undefined | null): boolean =>
  !!role && MODERATOR_ROLES.has(role);

/**
 * Rank of a chat role key (a member's `label ?? role`, or a message's `senderRole`). Mirrors the
 * server's `chatRoleRank`: owners outrank admins, admins outrank operators, everyone else is 0.
 */
const CHAT_ROLE_RANKS = new Map<string, number>([
  [ChatSenderRole.OWNER, 3],
  [ChatSenderRole.ADMIN, 2],
  [ChatSenderRole.OPERATOR, 1],
]);

export const chatRoleRank = (key?: string | null): number =>
  (key && CHAT_ROLE_RANKS.get(key)) || 0;

/**
 * Whether the viewer is offered Delete on a message: always their own, otherwise only when they
 * strictly outrank the sender (by the sender's role when it was sent). The server has the final say.
 */
export function canDeleteChatMessage(
  message: Pick<ChatMessageView, 'deleted' | 'senderRole'>,
  mine: boolean,
  viewer: Pick<ChatMembership, 'role' | 'label'> | null | undefined,
): boolean {
  if (message.deleted) return false;
  if (mine) return true;
  if (!viewer) return false;
  return (
    chatRoleRank(viewer.label ?? viewer.role) > chatRoleRank(message.senderRole)
  );
}

/** "99+" above 99, nothing at 0. */
export function formatUnread(unread: number): string | null {
  if (unread <= 0) return null;
  return unread > CHAT_UNREAD_BADGE_MAX
    ? `${CHAT_UNREAD_BADGE_MAX}+`
    : String(unread);
}

/** The row's second line: "Name: text", or just the text for system messages. */
export function inboxPreview(item: Pick<InboxItem, 'lastMessage'>): string {
  const last = item.lastMessage;
  if (!last) return '';
  return last.senderName ? `${last.senderName}: ${last.preview}` : last.preview;
}

/** Chats that open in the generic conversation view (they have no screen of their own). */
export const isSocialConversation = (type: ConversationType): boolean =>
  type === ConversationType.DIRECT || type === ConversationType.GROUP;

/** What the bottom of a conversation shows instead of (or as) the composer. */
export enum ComposerGate {
  OPEN = 'OPEN',
  /** The recipient of a message request: Accept / Decline / Block. */
  REQUEST = 'REQUEST',
  /** The sender of a DM request already sent their one message: wait for them to accept. */
  REQUEST_SENT = 'REQUEST_SENT',
  /** Posting policy ADMINS and the user is a plain member. */
  ADMINS_ONLY = 'ADMINS_ONLY',
  /** Deleted clan/team: readable, nobody posts. */
  READ_ONLY = 'READ_ONLY',
  /** Finished tournament/match. */
  CLOSED = 'CLOSED',
  /** The user was removed while the chat was open. */
  REMOVED = 'REMOVED',
}

export function composerGate(
  detail: Pick<
    ChatConversationDetail,
    | 'status'
    | 'settings'
    | 'membership'
    | 'awaitingAcceptance'
    | 'requestMessageSent'
  >,
  removed = false,
): ComposerGate {
  if (removed) return ComposerGate.REMOVED;
  if (detail.membership.state === ChatMemberState.REQUEST)
    return ComposerGate.REQUEST;
  if (detail.status === ConversationStatus.READ_ONLY)
    return ComposerGate.READ_ONLY;
  if (detail.status !== ConversationStatus.OPEN) return ComposerGate.CLOSED;
  if (detail.awaitingAcceptance && detail.requestMessageSent)
    return ComposerGate.REQUEST_SENT;
  if (
    detail.settings?.postingPolicy === ChatPostingPolicy.ADMINS &&
    !isModeratorRole(detail.membership.role)
  )
    return ComposerGate.ADMINS_ONLY;
  return ComposerGate.OPEN;
}

/** In-app path of the live object behind a share card, or null when the share can't link. */
export function sharedObjectPath(
  shared: Pick<ChatSharedObject, 'kind' | 'id'> & {
    snapshot?: Pick<ChatSharedObject['snapshot'], 'username'>;
  },
): string | null {
  const id = encodeURIComponent(shared.id);
  switch (shared.kind) {
    case SharedObjectKind.FEED_POST:
      return `/app/post/${id}`;
    case SharedObjectKind.TOURNAMENT:
      return `/tournament/${id}`;
    case SharedObjectKind.CLAN:
      // The clan profile resolves an id as well as a slug.
      return `/clan-profile/${id}`;
    case SharedObjectKind.PROFILE: {
      // The profile route is keyed by username; older shares don't carry one.
      const username = shared.snapshot?.username;
      return username ? `/profile/${encodeURIComponent(username)}` : null;
    }
    case SharedObjectKind.TEAM:
      // The team profile resolves an id as well as a slug.
      return `/team-profile/${id}`;
    case SharedObjectKind.ORGANIZATION:
      // The organization profile resolves an id as well as a slug.
      return `/org/${id}`;
    default:
      return null;
  }
}

export interface InboxFrameContext {
  myUserId?: string;
  /** The conversation on screen: its new messages are read as they arrive. */
  openConversationId?: string | null;
  /** The row preview for a live message (the server's is built the same way, in English). */
  previewOf: (message: ChatMessageView) => string;
}

export interface InboxFrameResult {
  items: InboxItem[];
  /** The frame touched something the cached rows can't express: refetch the inbox. */
  refetch: boolean;
}

const byNewest = (a: InboxItem, b: InboxItem) =>
  Date.parse(b.lastMessageAt) - Date.parse(a.lastMessageAt);

/**
 * Apply one socket frame to the cached inbox rows. Pure: returns the same array when nothing
 * changed, so callers can skip a cache write.
 */
export function applyFrameToInbox(
  items: InboxItem[],
  frame: ChatServerFrame,
  context: InboxFrameContext,
): InboxFrameResult {
  if (!('conversationId' in frame)) return { items, refetch: false };
  const index = items.findIndex(i => i.conversationId === frame.conversationId);
  const row = index >= 0 ? items[index] : null;

  switch (frame.op) {
    case ChatServerOp.MESSAGE: {
      const { message } = frame;
      // A hidden stub never surfaces, not even as a preview.
      if (message.hidden) return { items, refetch: false };
      if (!row) return { items, refetch: true };
      if (row.lastMessage && row.lastMessage.seq >= message.seq)
        return { items, refetch: false };
      const mine =
        !!context.myUserId && message.sender?.id === context.myUserId;
      const reading = context.openConversationId === row.conversationId;
      const senderName = message.sender
        ? message.sender.name || message.sender.username
        : undefined;
      const updated: InboxItem = {
        ...row,
        lastMessage: {
          id: message.id,
          seq: message.seq,
          from: message.sender?.id ?? null,
          type: message.type,
          preview: context.previewOf(message),
          at: message.createdAt,
          ...(senderName ? { senderName } : {}),
        },
        lastMessageAt: message.createdAt,
        unread: mine || reading ? row.unread : row.unread + 1,
      };
      const next = items.slice();
      next[index] = updated;
      return { items: next.sort(byNewest), refetch: false };
    }
    case ChatServerOp.READ: {
      if (!row) return { items, refetch: false };
      const lastSeq = row.lastMessage?.seq ?? 0;
      const unread = Math.min(row.unread, Math.max(0, lastSeq - frame.seq));
      if (unread === row.unread) return { items, refetch: false };
      const next = items.slice();
      next[index] = { ...row, unread };
      return { items: next, refetch: false };
    }
    case ChatServerOp.REMOVED:
      if (!row) return { items, refetch: false };
      return {
        items: items.filter(i => i.conversationId !== frame.conversationId),
        refetch: false,
      };
    case ChatServerOp.MESSAGE_DELETED:
      // Only the row's preview can change, and only when its last message went.
      return {
        items,
        refetch: !!row && row.lastMessage?.id === frame.messageId,
      };
    case ChatServerOp.CONVERSATION_UPDATED:
      return { items, refetch: !!row };
    default:
      return { items, refetch: false };
  }
}

/** Clear a row's unread badge once the user opens it. */
export function markInboxRowRead(
  items: InboxItem[],
  conversationId: string,
): InboxItem[] {
  const index = items.findIndex(i => i.conversationId === conversationId);
  if (index < 0 || items[index].unread === 0) return items;
  const next = items.slice();
  next[index] = { ...items[index], unread: 0 };
  return next;
}

/** The Messages page, optionally with one conversation open. */
export const MESSAGES_PATH = '/messages';
export const chatConversationPath = (conversationId: string): string =>
  `${MESSAGES_PATH}/${encodeURIComponent(conversationId)}`;

/**
 * Only public objects can be shared into chats. Feed cards are public; a brand ad in a restricted
 * category says so with `share.enabled: false`, and is never offered.
 */
export function canSendFeedPostInChat(post: { share: object }): boolean {
  return !('enabled' in post.share) || post.share.enabled !== false;
}

interface RosterLike {
  players?: { id: string }[];
  substitutes?: { id: string }[];
  coach?: { id: string }[];
  manager?: { id: string }[];
  captain?: { id: string } | null;
}

/**
 * Whether a user is on a team's roster: the same people the team chat (and, across a clan's
 * teams, the clan chat) is made of, alongside the clan's owners and admins.
 */
export function rosterHasUser(
  members: RosterLike | null | undefined,
  userId: string | undefined,
): boolean {
  if (!members || !userId) return false;
  const all = [
    ...(members.players ?? []),
    ...(members.substitutes ?? []),
    ...(members.coach ?? []),
    ...(members.manager ?? []),
    ...(members.captain ? [members.captain] : []),
  ];
  return all.some(member => member?.id === userId);
}
